import React, { useState } from 'react';
import axios from 'axios';
import { Mail, Check, Loader2, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SubscribeWidget = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [message, setMessage] = useState('');

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus('loading');
    setMessage('');
    try {
      const { data } = await axios.post(`${API}/subscribe`, {
        email: email.trim(),
        name: name.trim(),
        source: 'homepage-widget',
      });
      setStatus('success');
      if (data.status === 'already_subscribed') {
        setMessage("You're already on the list — thank you!");
      } else if (data.status === 'reactivated') {
        setMessage("Welcome back! You're subscribed again.");
      } else {
        setMessage("Check your inbox for a welcome email.");
      }
      setEmail('');
      setName('');
    } catch (err) {
      setStatus('error');
      setMessage(err?.response?.data?.detail || 'Something went wrong. Please try again.');
    }
  };

  return (
    <section className="relative py-20" data-testid="subscribe-widget">
      <div className="aurora-orb w-[420px] h-[420px] -top-10 left-1/4 bg-sky-300/40" />
      <div className="aurora-orb w-[360px] h-[360px] bottom-0 right-1/4 bg-orange-200/40" />

      <div className="relative max-w-3xl mx-auto px-6">
        <div className="glass-strong rounded-[2rem] p-8 md:p-12 text-center relative overflow-hidden">
          {/* corner glow */}
          <div className="absolute -top-16 -right-16 w-56 h-56 bg-gradient-to-br from-sky-400 to-indigo-500 rounded-full opacity-20 blur-3xl" />
          <div className="absolute -bottom-16 -left-16 w-56 h-56 bg-gradient-to-br from-orange-300 to-pink-400 rounded-full opacity-20 blur-3xl" />

          <div className="relative">
            <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 mb-5">
              <Sparkles size={13} className="text-sky-500" />
              <span className="text-[11px] uppercase tracking-widest font-bold text-sky-600">Weekly digest</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold tracking-[-0.04em] text-slate-900 leading-[1.05]">
              Paisley, <span className="font-serif-accent italic font-light bg-gradient-to-r from-sky-500 via-indigo-500 to-fuchsia-500 bg-clip-text text-transparent">in your inbox</span>.
            </h2>
            <p className="mt-4 text-base md:text-lg text-slate-600 max-w-xl mx-auto leading-relaxed">
              The week's best news, events and stories from Paisley and Renfrewshire — sent every Friday. No spam, unsubscribe in one click.
            </p>

            {status === 'success' ? (
              <div className="mt-8 inline-flex items-center gap-2 glass rounded-2xl px-5 py-3.5 text-slate-800 text-sm font-medium" data-testid="subscribe-success">
                <span className="w-7 h-7 rounded-full bg-emerald-500 flex items-center justify-center">
                  <Check size={14} className="text-white" />
                </span>
                {message || "You're subscribed!"}
              </div>
            ) : (
              <form onSubmit={onSubmit} className="mt-8 max-w-xl mx-auto" data-testid="subscribe-form">
                <div className="flex flex-col sm:flex-row gap-2.5 items-stretch">
                  <div className="relative flex-1">
                    <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="w-full glass rounded-full pl-11 pr-4 py-3.5 text-sm font-medium text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-400/60 transition"
                      data-testid="subscribe-email-input"
                      disabled={status === 'loading'}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={status === 'loading' || !email}
                    className="inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 disabled:opacity-60 disabled:cursor-not-allowed text-white px-7 py-3.5 rounded-full text-sm font-semibold tracking-tight transition-all hover:scale-[1.02]"
                    data-testid="subscribe-submit-btn"
                  >
                    {status === 'loading' ? (
                      <>
                        <Loader2 size={15} className="animate-spin" />
                        Subscribing…
                      </>
                    ) : (
                      <>Subscribe</>
                    )}
                  </button>
                </div>
                {/* Optional name field */}
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="First name (optional)"
                  className="mt-2.5 w-full glass rounded-full px-4 py-3 text-sm font-medium text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-400/60 transition"
                  data-testid="subscribe-name-input"
                  disabled={status === 'loading'}
                />
                {status === 'error' && (
                  <p className="mt-3 text-sm text-rose-600 font-medium" data-testid="subscribe-error">{message}</p>
                )}
                <p className="mt-4 text-xs text-slate-500">
                  By subscribing you agree to receive occasional emails from paisley.org.uk. We'll never share your address.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SubscribeWidget;
